import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase, CarTask, CarTaskActionType, CarTaskCategory, Reminder } from '../lib/supabase';
import { useCarTasks } from '../hooks/useCarTasks';
import { useAuth } from '../contexts/AuthContext';
import { Plus, ArrowLeft, Save, Trash2, Calendar, Activity, Loader2, ChevronDown, ChevronUp, Bell, BellOff } from 'lucide-react';
import { NavigationBar } from '../components/ui/NavigationBar';
import { PageTransition } from '../components/ui/PageTransition';
import { theme, styles } from '../lib/theme';
import { useTranslations } from '../i18n';
import { AlertModal } from '../components/ui/AlertModal';
import { useLogEntries } from '../hooks/useCarData';
import { estimateDateAtMileage } from '../lib/mileageUtils';
import { syncTaskReminders } from '../lib/taskReminders';
import { format, addMonths } from 'date-fns';

interface CarTaskFormProps {
  carId: string;
  taskId: string | null;
  onClose: () => void;
  existingTask?: CarTask;
}

export function CarTaskForm({ carId, taskId, onClose, existingTask }: CarTaskFormProps) {
  const { user } = useAuth();
  const { t } = useTranslations();
  const context = useOutletContext<any>() || {};
  const car = context.selectedCar || context.car;
  const { tasks, createTask, updateTask, deleteTask } = useCarTasks(carId);
  const { data: logEntries = [] } = useLogEntries(carId);
  const [showAdditional, setShowAdditional] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
      secondaryAction?: { label: string; onClick: () => void };
  }>({ isOpen: false, title: '', message: '' });

  type FormState = Partial<CarTask> & {
    has_different_first_interval?: boolean;
    last_performed_mileage?: number | string;
    last_performed_date?: string;
  };

  const [initialData, setInitialData] = useState<FormState>({});
  const [formData, setFormData] = useState<FormState>({
    name: '',
    description: '',
    category: null,
    action_type: 'replace',
    interval_distance: 15000,
    interval_months: 12,
    first_interval_distance: 15000,
    first_interval_months: 12,
    last_performed_mileage: '',
    last_performed_date: '',
    has_different_first_interval: false,
    history_confirmed: true,
    reminder_mileage: true,
    reminder_date: true
  });

  useEffect(() => {
    const task = existingTask || (taskId && taskId !== 'new' ? tasks.find((t: CarTask) => t.id === taskId) : undefined);
    if (task) {
      const hasDiff = task.first_interval_distance !== task.interval_distance || task.first_interval_months !== task.interval_months;
      const data: FormState = {
        name: task.name,
        description: task.description || '',
        category: task.category,
        action_type: task.action_type || 'replace',
        interval_distance: task.interval_distance || 0,
        interval_months: task.interval_months || 0,
        first_interval_distance: task.first_interval_distance || task.interval_distance || 0,
        first_interval_months: task.first_interval_months || task.interval_months || 0,
        last_performed_mileage: task.last_performed_mileage || '',
        last_performed_date: task.last_performed_date || '',
        has_different_first_interval: Boolean(hasDiff),
        history_confirmed: task.history_confirmed !== false,
        reminder_mileage: task.reminder_mileage || false,
        reminder_date: task.reminder_date || false
      };
      setFormData(data);
      setInitialData(data);
    } else if (taskId === 'new') {
      const defaultData: FormState = {
        name: '',
        description: '',
        category: null,
        action_type: 'replace',
        interval_distance: 15000,
        interval_months: 12,
        first_interval_distance: 15000,
        first_interval_months: 12,
        last_performed_mileage: '',
        last_performed_date: '',
        has_different_first_interval: false,
        history_confirmed: true,
        reminder_mileage: true,
        reminder_date: true
      };
      setFormData(defaultData);
      setInitialData(defaultData);
    }
  }, [taskId, tasks, existingTask]);

  const isDirty = taskId === 'new' ? !!formData.name : JSON.stringify(formData) !== JSON.stringify(initialData);

  const categories: CarTaskCategory[] = [ // using cast to keep list exhaustive
    'fluids', 'filters', 'engine', 'brakes', 'suspension', 'transmission', 'tyres', 'electrical', 'inspection', 'other'
  ];

  const actionTypes: CarTaskActionType[] = ['inspect', 'replace', 'service'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !carId || !user) return;
    
    setIsSubmitting(true);
    try {
      const payload = {
          name: formData.name,
          description: formData.description || null,
          category: formData.category ? (formData.category as CarTaskCategory) : null,
          action_type: formData.action_type as CarTaskActionType,
          interval_distance: formData.interval_distance ? Number(formData.interval_distance) : null,
          interval_months: formData.interval_months ? Number(formData.interval_months) : null,
          first_interval_distance: formData.has_different_first_interval 
            ? (formData.first_interval_distance ? Number(formData.first_interval_distance) : null) 
            : (formData.interval_distance ? Number(formData.interval_distance) : null),
          first_interval_months: formData.has_different_first_interval 
            ? (formData.first_interval_months ? Number(formData.first_interval_months) : null) 
            : (formData.interval_months ? Number(formData.interval_months) : null),
          last_performed_mileage: (formData.history_confirmed && formData.last_performed_mileage !== '' && formData.last_performed_mileage !== null) 
            ? Number(formData.last_performed_mileage) 
            : null,
          last_performed_date: formData.history_confirmed ? (formData.last_performed_date || null) : null,
          history_confirmed: formData.history_confirmed,
          reminder_mileage: formData.reminder_mileage,
          reminder_date: formData.reminder_date
      };

      let savedId = taskId;
      if (taskId && taskId !== 'new') {
        await updateTask.mutateAsync({ id: taskId, updates: payload });
      } else {
        const result = await createTask.mutateAsync({
          car_id: carId,
          user_id: user.id,
          ...payload,
          is_distance_locked: false,
          ai_generated: false
        });
        savedId = result.id;
      }

      // Sync Reminders
      if (savedId) {
        const fullTaskRecord = { ...payload, id: savedId, car_id: carId, user_id: user.id } as CarTask;
        await syncTaskReminders(car, fullTaskRecord, logEntries);
      }

      onClose();
    } catch (error: any) {
      console.error('Failed to save task:', error);
      setAlertConfig({
        isOpen: true,
        title: t.common?.error || 'Error',
        message: error.message || t.carTasks.form.saveError,
        confirmLabel: t.common.ok
      });
    } finally {
      setIsSubmitting(false);
    }
  };

    const handleDelete = async () => {
    if (!taskId || taskId === 'new') return;
    
    setAlertConfig({
      isOpen: true,
      title: t.carTasks.form.deleteTitle,
      message: t.carTasks.form.deleteConfirm,
      confirmLabel: t.common.cancel,
      secondaryAction: {
        label: t.common.delete,
        onClick: async () => {
          setIsSubmitting(true);
          try {
            await deleteTask.mutateAsync(taskId);
            onClose();
          } catch (error: any) {
            console.error('Failed to delete task', error);
            setAlertConfig({
              isOpen: true,
              title: t.common.error,
              message: error.message || t.carTasks.form.saveError,
              confirmLabel: t.common.ok
            });
          } finally {
            setIsSubmitting(false);
          }
        }
      }
    });
  };

  const reminderDates = useMemo(() => {
    const res: { 
      mileage: string | null; 
      date: string | null; 
      mileageError?: string;
      dateError?: string;
      mileageTarget?: number;
    } = { mileage: null, date: null };
    
    const intervalDist = Number(formData.interval_distance) || 0;
    const intervalMo = Number(formData.interval_months) || 0;
    const firstDist = (formData.has_different_first_interval ? Number(formData.first_interval_distance) : intervalDist) || intervalDist;
    const firstMo = (formData.has_different_first_interval ? Number(formData.first_interval_months) : intervalMo) || intervalMo;

    if (!intervalDist && !intervalMo) return res;

    let startMileage = 0;
    let startDate: Date | null = null;

    if (formData.history_confirmed) {
        if (!formData.last_performed_mileage && !formData.last_performed_date) return res; // Should be handled by isInfoMissing anyway
        
        if (formData.last_performed_mileage) {
            startMileage = Number(formData.last_performed_mileage);
        } else {
            res.mileageError = t.carTasks.form.missingLastMileage || 'Missing last performed mileage';
        }

        if (formData.last_performed_date) {
            startDate = new Date(formData.last_performed_date as string);
        } else {
            res.dateError = t.carTasks.form.missingLastDate || 'Missing last performed date';
        }
    } else {
        // Never performed - use 0 and car year
        startMileage = 0;
        if (car?.year) {
            startDate = new Date(car.year, 0, 1); // Jan 1st of production year
        } else if (car?.bought_at) {
            startDate = new Date(car.bought_at);
        }
    }

    // Mileage reminder
    if (intervalDist) {
        if (formData.history_confirmed && !formData.last_performed_mileage) {
            // Error already set
        } else {
            const isFirst = !formData.history_confirmed;
            const target = startMileage + (isFirst ? firstDist : intervalDist);
            res.mileageTarget = target;
            const est = estimateDateAtMileage(car, logEntries, target);
            res.mileage = est ? format(est, 'dd.MM.yyyy') : null;
        }
    }

    // Date reminder
    if (intervalMo) {
        if (formData.history_confirmed && !formData.last_performed_date) {
            // Error already set
        } else if (startDate) {
            const isFirst = !formData.history_confirmed;
            const next = addMonths(startDate, (isFirst ? firstMo : intervalMo));
            res.date = format(next, 'dd.MM.yyyy');
        } else {
            res.dateError = t.carTasks.form.missingStartDate || 'Could not determine start date';
        }
    }

    return res;
  }, [car, logEntries, formData, taskId, existingTask, t]);


  const inputClass = "p-4 bg-white dark:bg-slate-900 border border-gray-100 dark:border-white/5 rounded-2xl focus:ring-2 focus:ring-blue-500 font-medium text-gray-900 dark:text-white placeholder-gray-400";
  const primaryBtnClass = "bg-blue-600 hover:bg-blue-700 text-white rounded-[2rem] font-bold shadow-lg shadow-blue-500/20";

  const isInfoMissing = formData.history_confirmed && !formData.last_performed_mileage && !formData.last_performed_date;

  return (
    <PageTransition>
      <div className={`fixed inset-0 z-[70] flex flex-col ${theme.colors.pageBg}`}>
        <div className="flex-shrink-0 z-30">
            <NavigationBar
                title={taskId && taskId !== 'new' ? t.common.edit : t.carTasks.form.newTitle}
                onBack={onClose}
                fixed={false}
                rightAction={isDirty ? {
                  icon: isSubmitting ? Loader2 : Save,
                  onClick: () => {
                    const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
                    handleSubmit(fakeEvent);
                  },
                  disabled: isSubmitting || !formData.name,
                  className: isSubmitting ? 'animate-spin text-blue-600 dark:text-blue-400' : 'text-blue-600 dark:text-blue-400'
                } : undefined}
            />
        </div>
        
        <div className="flex-1 overflow-y-auto w-full">
            <form onSubmit={handleSubmit} className="p-4 max-w-2xl mx-auto space-y-6 pb-32">
                <div className="space-y-4">
                  <div>
                    <label className={theme.typography.sectionLabel}>{t.expenses.expenseTitle}</label>
                    <input
                        type="text"
                        required
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`${inputClass} w-full mt-1.5`}
                        placeholder="e.g. Oil change"
                    />
                  </div>

                  <div>
                    <label className={theme.typography.sectionLabel}>{t.carTasks.form.actionType}</label>
                    <div className="grid grid-cols-3 gap-2 mt-1.5">
                        {actionTypes.map(act => (
                          <button
                            key={act}
                            type="button"
                            onClick={() => setFormData({ ...formData, action_type: act })}
                            className={`p-3 rounded-xl font-bold text-[13px] capitalize border-2 transition-all ${
                              formData.action_type === act 
                                ? 'bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400'
                                : 'bg-white border-transparent text-slate-500 dark:bg-slate-900 dark:text-slate-400'
                            }`}
                          >
                            {act}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div>
                    <label className={theme.typography.sectionLabel}>{t.settings.categories}</label>
                    <select
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value ? (e.target.value as CarTaskCategory) : null })}
                        className={`${inputClass} w-full mt-1.5 capitalize`}
                    >
                        <option value="">- {t.carMetadata.bodyTypes.not_specified} -</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                      <div>
                          <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}><Calendar size={14}/> {t.carTasks.form.intervalMonths}</label>
                          <input
                              type="number"
                              min="0"
                              value={formData.interval_months || ''}
                              onChange={(e) => setFormData({ ...formData, interval_months: parseInt(e.target.value) || 0 })}
                              className={`${inputClass} w-full mt-1.5`}
                              placeholder="12"
                          />
                      </div>
                      <div>
                          <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}><Activity size={14}/> {t.carTasks.form.intervalDistance}</label>
                          <input
                              type="number"
                              step="500"
                              min="0"
                              value={formData.interval_distance || ''}
                              onChange={(e) => setFormData({ ...formData, interval_distance: parseInt(e.target.value) || 0 })}
                              className={`${inputClass} w-full mt-1.5`}
                              placeholder="15000"
                          />
                      </div>
                  </div>

                  <div className={styles.card}>
                    <div className="p-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.has_different_first_interval || false}
                          onChange={(e) => setFormData({ ...formData, has_different_first_interval: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 dark:border-white/10 dark:bg-slate-800"
                        />
                        <span className="font-semibold text-gray-900 dark:text-white">{t.carTasks.form.diffFirstInterval}</span>
                      </label>
                      
                      {formData.has_different_first_interval && (
                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                            <div>
                                <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}>{t.carTasks.form.firstInterval} (mo)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.first_interval_months || ''}
                                    onChange={(e) => setFormData({ ...formData, first_interval_months: parseInt(e.target.value) || 0 })}
                                    className={`${inputClass} w-full mt-1.5`}
                                    placeholder="6"
                                />
                            </div>
                            <div>
                                <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}>{t.carTasks.form.firstInterval} (dist)</label>
                                <input
                                    type="number"
                                    step="500"
                                    min="0"
                                    value={formData.first_interval_distance || ''}
                                    onChange={(e) => setFormData({ ...formData, first_interval_distance: parseInt(e.target.value) || 0 })}
                                    className={`${inputClass} w-full mt-1.5`}
                                    placeholder="5000"
                                />
                            </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.card}>
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{t.carTasks.form.historyToggle}</h4>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={formData.history_confirmed || false} 
                                    onChange={(e) => setFormData({ ...formData, history_confirmed: e.target.checked })}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                        
                        {formData.history_confirmed && (
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-white/5 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div>
                                    <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}><Calendar size={14}/> {t.expenses.date}</label>
                                    <input
                                        type="date"
                                        value={formData.last_performed_date || ''}
                                        onChange={(e) => setFormData({ ...formData, last_performed_date: e.target.value })}
                                        className={`${inputClass} w-full mt-1.5`}
                                        max={new Date().toISOString().split("T")[0]}
                                    />
                                </div>
                                <div>
                                    <label className={`${theme.typography.sectionLabel} flex items-center gap-1.5`}><Activity size={14}/> {t.expenses.mileage} ({car?.distance_unit || 'km'})</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.last_performed_mileage || ''}
                                        onChange={(e) => setFormData({ ...formData, last_performed_mileage: parseInt(e.target.value) || '' })}
                                        className={`${inputClass} w-full mt-1.5`}
                                        placeholder="Piem. 150000"
                                    />
                                </div>
                            </div>
                        )}
                        
                        {!formData.history_confirmed && (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
                                {t.carTasks.form.historyToggleDesc}
                            </p>
                        )}
                    </div>
                  </div>

                  {!isInfoMissing && (
                    <div className="pt-4">
                        <div className="mb-2 px-1">
                            <span className={theme.typography.sectionTitle}>{t.carTasks.form.reminders}</span>
                        </div>

                        <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                            <div className={`flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5`}>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-base font-medium text-gray-900 dark:text-white">
                                        {t.carTasks.form.notifyDate}
                                    </span>
                                    {reminderDates.date && (
                                        <span className="text-[11px] text-blue-500 dark:text-blue-400 font-medium">
                                            {t.carTasks.form.nextDateReminder}: {reminderDates.date}
                                        </span>
                                    )}
                                    {reminderDates.dateError && (
                                        <span className="text-[10px] text-amber-500 italic">
                                            {reminderDates.dateError}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={formData.reminder_date}
                                    onClick={() => setFormData({ ...formData, reminder_date: !formData.reminder_date })}
                                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.reminder_date ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.reminder_date ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>

                            <div className={`flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5`}>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-base font-medium text-gray-900 dark:text-white">
                                        {t.carTasks.form.notifyMileage}
                                    </span>
                                    {reminderDates.mileage && (
                                        <div className="flex flex-col">
                                            <span className="text-[11px] text-blue-500 dark:text-blue-400 font-medium">
                                                {t.carTasks.form.nextMileageReminder}: {reminderDates.mileageTarget?.toLocaleString()} {car?.distance_unit || 'km'}
                                            </span>
                                            <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                                {t.carTasks.form.estimatedDate}: {reminderDates.mileage}
                                            </span>
                                        </div>
                                    )}
                                    {reminderDates.mileageError && (
                                        <span className="text-[10px] text-amber-500 italic">
                                            {reminderDates.mileageError}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={formData.reminder_mileage}
                                    onClick={() => setFormData({ ...formData, reminder_mileage: !formData.reminder_mileage })}
                                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.reminder_mileage ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.reminder_mileage ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                  )}


                  <div>
                    <label className={theme.typography.sectionLabel}>{t.expenses.description}</label>

                    <textarea
                        rows={3}
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className={`${inputClass} w-full mt-1.5 resize-none`}
                        placeholder="e.g. Specifications or notes"
                    />
                  </div>

                  <div className="pt-4">
                    <div className="mb-2 px-1">
                        <span className={theme.typography.sectionTitle}>{t.carTasks.form.additionalOptions}</span>
                    </div>

                    <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5">
                        {taskId && taskId !== 'new' && (
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-between p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                            >
                                <span>{t.carTasks.form.deleteTitle}</span>
                                <Trash2 size={18} />
                            </button>
                        )}
                        {/* Placeholder for other potential options */}
                    </div>
                </div>
              </div>
            </form>

            <AlertModal
              {...alertConfig}
              onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
      </div>
    </PageTransition>
  );
}
